import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, UserMinus, Users } from 'lucide-react';

import {
  assistantsService,
  type EquipeBreve,
  type Membres,
  type Personne,
} from '../../services/api/assistants';

interface Props {
  projetId: number;
  onErreur: (titre: string, message: string) => void;
}

/**
 * Gestion des accès à une activité.
 *
 * Deux façons d'ouvrir l'accès : inscrire une personne, ou rattacher une
 * équipe entière. La seconde évite d'inscrire chaque agent sur chaque activité
 * de son service — c'est elle qui rend la notion d'équipe utile plutôt que
 * décorative.
 */
const MembresActivite: React.FC<Props> = ({ projetId, onErreur }) => {
  const [etat, setEtat] = useState<Membres | null>(null);
  const [candidats, setCandidats] = useState<Personne[]>([]);
  const [equipes, setEquipes] = useState<EquipeBreve[]>([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [choix, setChoix] = useState('');

  const recharger = useCallback(async () => {
    try {
      const [membres, disponibles] = await Promise.all([
        assistantsService.listerMembres(projetId),
        assistantsService.listerCandidats(projetId),
      ]);
      setEtat(membres);
      setCandidats(disponibles.utilisateurs);
      setEquipes(disponibles.equipes);
    } catch (erreur: any) {
      onErreur('Chargement impossible', erreur.message);
    } finally {
      setChargement(false);
    }
  }, [projetId, onErreur]);

  useEffect(() => {
    recharger();
  }, [recharger]);

  /** Enveloppe commune : verrouille l'écran, agit, recharge l'état réel. */
  const agir = async (action: () => Promise<unknown>, titre: string) => {
    setEnCours(true);
    try {
      await action();
      await recharger();
      setChoix('');
    } catch (erreur: any) {
      onErreur(titre, erreur.message);
    } finally {
      setEnCours(false);
    }
  };

  if (chargement) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-eg-gris">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement des accès...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section>
        <div className="mb-2 flex items-center gap-2 text-[13px] font-medium text-eg-encre">
          <Users className="h-4 w-4 text-eg-accent" />
          Équipe rattachée
        </div>
        <p className="mb-2 text-xs text-eg-gris">
          Tous les membres de l'équipe choisie accèdent à l'activité, sans avoir à
          être inscrits un par un.
        </p>

        {equipes.length === 0 ? (
          <p className="rounded-xl bg-eg-papier px-3 py-2 text-[13px] text-eg-gris">
            Vous n'appartenez à aucune équipe pour l'instant.{' '}
            <Link to="/workspace/teams" className="text-eg-accent underline hover:text-eg-encre">
              En créer une
            </Link>
            .
          </p>
        ) : (
          <select
            value={etat?.equipe?.id ?? ''}
            disabled={enCours}
            onChange={(e) =>
              agir(
                () =>
                  assistantsService.rattacherEquipe(
                    projetId,
                    e.target.value ? Number(e.target.value) : null,
                  ),
                'Rattachement impossible',
              )
            }
            className="w-full rounded-xl border-0 bg-eg-papier px-3.5 py-2 text-[13px] text-eg-encre
              focus:border-eg-accent focus:outline-none focus:ring-1 focus:ring-eg-accent
              disabled:opacity-50"
          >
            <option value="">Aucune équipe</option>
            {equipes.map((equipe) => (
              <option key={equipe.id} value={equipe.id}>
                {equipe.name}
              </option>
            ))}
          </select>
        )}
        {equipes.length > 0 && (
          <p className="mt-1.5 text-xs text-eg-gris">
            <Link to="/workspace/teams" className="text-eg-accent underline hover:text-eg-encre">
              Gérer mes équipes
            </Link>{' '}
            pour en créer une ou en modifier la composition.
          </p>
        )}
      </section>

      <section>
        <div className="mb-2 text-[13px] font-medium text-eg-encre">
          Personnes inscrites ({etat?.membres.length ?? 0})
        </div>

        <ul className="mb-3 divide-y divide-black/[0.05] rounded-xl bg-eg-papier">
          {etat?.membres.map((personne) => (
            <li key={personne.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] text-eg-encre">{personne.nom}</p>
                <p className="truncate text-xs text-eg-gris">{personne.email}</p>
              </div>
              {personne.createur ? (
                <span className="shrink-0 rounded-full bg-eg-voile px-2 py-0.5 text-xs text-eg-gris">
                  créateur
                </span>
              ) : (
                <button
                  type="button"
                  disabled={enCours}
                  onClick={() =>
                    agir(
                      () => assistantsService.retirerMembre(projetId, personne.id),
                      'Retrait impossible',
                    )
                  }
                  className="shrink-0 rounded-md p-1.5 text-eg-estompe transition-colors
                    hover:bg-eg-accent/10 hover:text-eg-accent disabled:opacity-50"
                  title={`Retirer ${personne.nom}`}
                >
                  <UserMinus className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
          {etat?.membres.length === 0 && (
            <li className="px-3 py-2 text-sm text-eg-gris">Personne pour l'instant.</li>
          )}
        </ul>

        <div className="flex gap-2">
          <select
            value={choix}
            disabled={enCours || candidats.length === 0}
            onChange={(e) => setChoix(e.target.value)}
            className="flex-1 rounded-xl border-0 bg-eg-papier px-3.5 py-2 text-[13px] text-eg-encre
              focus:border-eg-accent focus:outline-none focus:ring-1 focus:ring-eg-accent
              disabled:opacity-50"
          >
            <option value="">
              {candidats.length === 0
                ? 'Tout le monde est déjà inscrit'
                : 'Ajouter une personne...'}
            </option>
            {candidats.map((personne) => (
              <option key={personne.id} value={personne.id}>
                {personne.nom} — {personne.email}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={enCours || !choix}
            onClick={() =>
              agir(
                () => assistantsService.ajouterMembre(projetId, Number(choix)),
                'Ajout impossible',
              )
            }
            className="inline-flex items-center gap-1.5 rounded-full bg-eg-ombre px-4 py-2 text-[13px] hover:bg-eg-profond
              font-medium text-eg-blanc transition-colors
              disabled:opacity-50"
          >
            {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Ajouter
          </button>
        </div>
      </section>
    </div>
  );
};

export default MembresActivite;
